import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { userService } from './services/userService';
import { authService } from './services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [hasRefreshToken, setHasRefreshToken] = useState(false);

  // Hàm kiểm tra authentication khi app load
  const verifyAuth = useCallback(async () => {
    setLoading(true);
    try {
      // Gọi API để lấy thông tin user từ cookie
      // Thêm skipRefresh: true để interceptor không cố gắng gọi refresh-token nếu profile trả về 401
      const res = await userService.getProfile({ skipRefresh: true });
      const userData = res.data || res.user || res;

      setUser(userData);
      setSessionExpiresAt(userData.sessionExpiresAt);
      setHasRefreshToken(!!userData.hasRefreshToken);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setSessionExpiresAt(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyAuth();

    // Lắng nghe lệnh từ Axios Interceptor để ép thoát (khi RefreshToken hết hạn)
    const handleUnauthorized = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [verifyAuth]);

  // Hàm đăng nhập
  const login = async (credentials) => {
    const res = await authService.login(credentials);
    const userData = res.data?.user || res.data || res.user || res;

    setUser(userData);
    setSessionExpiresAt(userData.sessionExpiresAt);
    setHasRefreshToken(!!res.data?.hasRefreshToken || !!res.hasRefreshToken);
    setIsAuthenticated(true);
    return userData;
  };

  // Hàm đăng xuất
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setSessionExpiresAt(null);
    setHasRefreshToken(false);
    setIsAuthenticated(false);
  };

  // Hàm cập nhật user (dùng trong ProfileModal)
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const authValue = {
    user,
    isAuthenticated,
    loading,
    sessionExpiresAt,
    hasRefreshToken,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={authValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
